/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useCallback } from "react";

import TileLayer from "ol/layer/Tile";
import TileWMS from "ol/source/TileWMS";
import WMTS from "ol/source/WMTS";
import TilegridWMTS from "ol/tilegrid/WMTS";
import OlMap from "ol/Map";
import { View } from "ol";
import { Id } from "react-toastify";
import DragPan from "ol/interaction/DragPan";
import { defaults as defaultControls, Rotate } from "ol/control";

import { Coordinate } from "ol/coordinate";
import environments from "../../../../environments";

import { useOlMap } from "../../../../shared/hooks/useOlMap";

import {
    findLayerByTitle,
    googleMapsLayer,
    openStreetMapLayer,
    transformCoordinates,
    wmtsSettings,
} from "../../../../shared/utils/ol.utils";

import { LayersApiService } from "../../../../shared/services/api/layers-api.service";
import { useAuth } from "../../../../shared/hooks/useAuth";
import { StoragesService } from "../../../../shared/storages/storages.service";
import { storageConstants } from "../../../../shared/utils/storage.utils";
import { closeToast, showToast } from "../../../global/toast";
import { createCompassRose } from "../../../global/compass-rose";
import compass from "../../../../shared/assets/icons/compass.png";

interface OLMapProps {
    specificCenter?: Coordinate;
    children?: React.ReactNode;
}

const {
    geoserver: { url, ortoUrl, ortoAsPng },
    webgeo: {
        allowMapDragAtAnyZoom,
        map: { center, zoom },
    },
} = environments;

let hasBeenRendered = 0;
let dragDisabled = false;
let toastId: Id | null;

const span: HTMLSpanElement = createCompassRose(compass);

export const Map: React.FC<OLMapProps> = ({ specificCenter, children }) => {
    const { user } = useAuth();
    const { map, setMap } = useOlMap();
    const { signed } = useAuth();

    const createLayerSource = (
        cam_nome_geoserver: string,
        cam_cache: boolean
    ) => {
        const sicarUrl = `https://geoserver.car.gov.br/geoserver/sicar/wms`; // URL do geoserver sicar, para as camadas "sicar:"

        if (cam_nome_geoserver.includes("sicar:")) {
            // Sempre usa TileWMS para camadas SICAR
            return new TileWMS({
                url: sicarUrl,
                params: { LAYERS: cam_nome_geoserver },
                serverType: "geoserver",
                crossOrigin: "Anonymous",
                hidpi: false,
                transition: 0,
            });
        }

        if (cam_cache) {
            // Usa WMTS para camadas com cache
            return new WMTS({
                url: `${ortoUrl || url}/gwc/service/wmts`,
                layer: cam_nome_geoserver,
                matrixSet: "EPSG:4326",
                format: ortoAsPng ? "image/png" : "image/jpeg",
                tileGrid: new TilegridWMTS({
                    tileSize: [256, 256],
                    extent: [-180.0, -90.0, 180.0, 90.0],
                    origin: [-180.0, 90.0],
                    resolutions: wmtsSettings.resolutions,
                    matrixIds: wmtsSettings.matrixIds,
                }),
                crossOrigin: "Anonymous",
                projection: "EPSG:4326",
                style: "",
            });
        }

        // TileWMS padrão
        return new TileWMS({
            url: `${url}/wms`,
            params: { LAYERS: cam_nome_geoserver },
            serverType: "geoserver",
            crossOrigin: "Anonymous",
            hidpi: false,
            transition: 0,
        });
    };

    const loadLayers = useCallback(async () => {
        const layersReponse = await LayersApiService.getLayers(
            "orderBy=ASC:cam_ordem&"
        );

        layersReponse.data.forEach(({ cam_nome_geoserver, cam_cache }) => {
            // caso o usuário esteja logado só alterar a visibilidade das camadas habilitadas na tela de entrada
            const layer = findLayerByTitle(
                map || ({} as OlMap),
                cam_nome_geoserver
            );

            const savedLayers = StoragesService.localStorage.getValue(
                storageConstants.ENABLED_LAYERS(user?.id)
            );

            const isLayerVisible = savedLayers
                ? (JSON.parse(savedLayers).includes(
                      cam_nome_geoserver
                  ) as boolean)
                : false;

            if (layer) return layer?.setVisible(signed ? isLayerVisible : true);

            // console.log(cam_nome_geoserver.includes("sicar:"));
            // caso a camada tenha a opção de cache gerar wmts
            // eslint-disable-next-line no-nested-ternary
            const source = createLayerSource(cam_nome_geoserver, cam_cache);

            return map?.addLayer(
                new TileLayer({
                    properties: {
                        title: cam_nome_geoserver,
                    },
                    source,
                    visible: signed ? isLayerVisible : true,
                })
            );
        });
    }, [map, signed, user]);

    map?.on("moveend", () => {
        // console.log(map?.getView().getCenter());
        const currentZoom = map?.getView()?.getZoom() || 0;
        const dragShouldBeDiabled = allowMapDragAtAnyZoom
            ? false
            : currentZoom > 19.3;

        const dragInteraction = map
            ?.getInteractions()
            .getArray()
            .filter((it) => it instanceof DragPan);

        if (dragShouldBeDiabled && !dragDisabled) {
            toastId = showToast({
                type: "info",
                message: "Para movimentar o mapa, remova o zoom.",
                options: { autoClose: false },
            });
        }

        if (!dragShouldBeDiabled && dragDisabled && toastId) {
            closeToast(toastId);
            toastId = null;
        }

        dragInteraction.forEach((it) => {
            if (it.getActive() === dragShouldBeDiabled)
                it.setActive(!dragShouldBeDiabled);
        });

        dragDisabled = dragShouldBeDiabled;
    });

    useEffect(() => {
        if (map) {
            loadLayers();
        }
    }, [loadLayers, map]);

    useEffect(() => {
        if (hasBeenRendered < 1) {
            setMap(
                new OlMap({
                    controls: defaultControls().extend([
                        new Rotate({
                            label: span,
                            tipLabel: "CENTRALIZAR",
                            autoHide: false,
                        }),
                    ]),
                    layers: [openStreetMapLayer(), googleMapsLayer()],
                    target: "main-map",
                    view: new View({
                        // center: specificCenter, // DEV ONLY
                        center: specificCenter || transformCoordinates(center),
                        zoom,
                    }),
                })
            );
            hasBeenRendered += 1;
        }
    }, [setMap]);

    return (
        <div
            style={{ position: "relative", width: "100%", height: "100%" }}
            id="main-map"
        >
            {children}
        </div>
    );
};
